import { Component, Input, OnChanges, HostBinding, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ThemeService } from 'rev-portal/branding/Theme.Service';
import { getInitials } from 'rev-shared/util/nameUtil';

import styles from './ProfilePicture.module.less';

const Medium = 'medium';
const Large = 'large';
const DefaultIcon = 'user';

@Component({
	selector: 'vb-profile-picture',
	host: {
		'[class]': 'styles.root'
	},
	template: `
		<span
			[ngClass]="[
				styles.wrap,
				hilight ? 'theme-accent-border' : '',
				!url ? styles.wrapBackground : ''
			]"
			[style]="wrapStyle">
			<img
				*ngIf="url; else noImage"
				[ngClass]="styles.profilePicture"
				[alt]="altTxt || defaultAltTxt"
				[src]="url"
				loading="lazy"
				vbImageLoad
			/>

			<ng-template #noImage>
				<span *ngIf="placeholderInitials; else placeholderIcon"
					[ngClass]="[styles.placeholderInitials]"
					role="img"
					[attr.aria-label]="altTxt || defaultAltTxt">
					{{ placeholderInitials }}
				</span>
			</ng-template>

			<ng-template #placeholderIcon>
				<span
					class="glyphicons theme-accent-txt"
					[ngClass]="[styles.profileIcon, placeholderIconClass]"
					[hidden]="url"
					role="img"
					[attr.aria-label]="altTxt">
				</span>
			</ng-template>
		</span>
	`,
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class VbProfilePictureComponent implements OnChanges {
	@Input() public altTxt: string;
	@Input() public hilight: boolean;
	@Input() public wrapStyle: string;
	@Input() public profileUrl: string;
	@Input() public size: string;
	@Input() public placeholderIconClass: string;
	@Input() public placeholderInitialsFullName: string;

	public placeholderInitials: string;
	public url: string;
	public readonly styles = styles;
	public readonly defaultAltTxt = this.translateService.instant('UserProfile_ProfilePicture');

	constructor(
		private readonly ThemeService: ThemeService,
		private readonly translateService: TranslateService
	) {}

	private get userProfileImageDimensions(): string {
		return this.ThemeService.brandingSettings.headerSettings.userProfileImageDimensions;
	}

	public ngOnChanges(): void {
		this.url = this.profileUrl ?
			`${this.profileUrl}?size=${this.userProfileImageDimensions}` :
			undefined;

		this.placeholderIconClass = this.placeholderIconClass || DefaultIcon;
		this.placeholderInitials = getInitials(this.placeholderInitialsFullName);
	}

	@HostBinding('class.vbProfilePictureLarge')
	public get isLargeSize(): boolean {
		return this.size === Large;
	}

	@HostBinding('class.vbProfilePictureMedium')
	public get isMediumSize(): boolean {
		return this.size === Medium;
	}
}
